import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const getFirebaseToken = () => {
  return new Promise((resolve, reject) => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(user => {
      unsubscribe();
      if (user) {
        user.getIdToken().then(resolve).catch(reject);
      } else {
        reject(new Error("No user is signed in."));
      }
    });
  });
};

export const uploadAudio = async (audioFile, patientId, sessionNumber, originURL) => {
  console.log('[TranscriptionService] Starting uploadAudio...');
  console.log('[TranscriptionService] File:', audioFile.name, audioFile.type, audioFile.size, 'bytes');
  console.log('[TranscriptionService] PatientId:', patientId);
  console.log('[TranscriptionService] SessionNumber:', sessionNumber);
  console.log('[TranscriptionService] OriginURL:', originURL);

  const token = await getFirebaseToken();
  console.log('[TranscriptionService] Got Firebase token:', token ? 'YES ✓' : 'NO ✗');

  // Aponta para a função de upload (Cloud Functions v2 - southamerica-east1)
  const url = `https://southamerica-east1-pro-saude-mental.cloudfunctions.net/uploadAudio`;
  console.log('[TranscriptionService] Target URL:', url);

  const formData = new FormData();
  formData.append('file', audioFile);
  formData.append('patientId', patientId);
  if (sessionNumber) {
    formData.append('sessionNumber', sessionNumber);
  }
  if (originURL) {
    formData.append('originURL', originURL);
  }

  console.log('[TranscriptionService] FormData prepared, starting fetch...');

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    console.log('[TranscriptionService] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('[TranscriptionService] ERROR Response body:', errorBody);
      throw new Error(`Request failed with status ${response.status}: ${errorBody}`);
    }

    const result = await response.json();
    console.log('[TranscriptionService] SUCCESS! Result:', result);
    return result; // Retorna o objeto inteiro { data: { transcriptionId: '...' } }
  } catch (error) {
    console.error('[TranscriptionService] EXCEPTION:', error);
    throw new Error('Failed to transcribe audio.');
  }
};

export const getAudioMimeType = (file) => {
  const type = file.type ? file.type.toLowerCase() : '';
  const name = file.name ? file.name.toLowerCase() : '';

  if (type.includes('ogg') || name.endsWith('.ogg') || name.endsWith('.opus')) return 'audio/ogg';
  if (type.includes('mp3') || type.includes('mpeg') || name.endsWith('.mp3')) return 'audio/mp3';
  if (type.includes('m4a') || type.includes('mp4') || type.includes('aac') || name.endsWith('.m4a') || name.endsWith('.aac')) return 'audio/m4a';
  if (type.includes('wav') || name.endsWith('.wav')) return 'audio/wav';
  if (type.includes('webm') || name.endsWith('.webm')) return 'audio/webm';
  if (type.includes('flac') || name.endsWith('.flac')) return 'audio/flac';
  if (type.includes('amr') || name.endsWith('.amr')) return 'audio/amr';
  if (type.includes('3gpp') || type.includes('3gp') || name.endsWith('.3gp')) return 'audio/3gpp';

  return file.type || 'audio/mp3';
};

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const base64Data = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64Data);
      } else {
        reject(new Error("Falha ao converter arquivo para base64."));
      }
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * Obtém a chave da API Gemini via Cloud Function proxy seguro.
 * A chave nunca fica exposta no client — é obtida do Secret Manager pelo backend.
 */
const getGeminiApiKeyViaProxy = async () => {
  const token = await getFirebaseToken();
  const res = await fetch(
    'https://southamerica-east1-pro-saude-mental.cloudfunctions.net/getGeminiApiKey',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ data: {} })
    }
  );
  if (!res.ok) throw new Error(`Falha ao obter API Key: ${res.status}`);
  const json = await res.json();
  return json.apiKey || json.result?.apiKey || json.data?.apiKey;
};

/**
 * Faz upload de um arquivo de áudio para a Gemini File API e retorna o fileUri.
 * Isso evita o erro 413 (Content Too Large) de enviar base64 enorme pelo proxy.
 */
const uploadToGeminiFileApi = async (audioFile, apiKey) => {
  const mimeType = getAudioMimeType(audioFile);
  const displayName = audioFile.name || 'audio_session';

  console.log(`[TranscriptionService] Uploading ${displayName} (${(audioFile.size / 1024 / 1024).toFixed(2)} MB) to Gemini File API...`);

  // Fase 1: Iniciar upload resumível
  const startRes = await fetch(
    `https://generativelanguage.googleapis.com/upload/v1beta/files?key=${apiKey}`,
    {
      method: 'POST',
      headers: {
        'X-Goog-Upload-Protocol': 'resumable',
        'X-Goog-Upload-Command': 'start',
        'X-Goog-Upload-Header-Content-Length': audioFile.size.toString(),
        'X-Goog-Upload-Header-Content-Type': mimeType,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ file: { display_name: displayName } })
    }
  );

  if (!startRes.ok) {
    const errText = await startRes.text();
    throw new Error(`Falha ao iniciar upload na File API (${startRes.status}): ${errText}`);
  }

  const uploadUrl = startRes.headers.get('X-Goog-Upload-URL');
  if (!uploadUrl) {
    throw new Error('Gemini File API não retornou a URL de upload.');
  }

  // Fase 2: Enviar bytes do arquivo
  const uploadRes = await fetch(uploadUrl, {
    method: 'POST',
    headers: {
      'Content-Length': audioFile.size.toString(),
      'X-Goog-Upload-Offset': '0',
      'X-Goog-Upload-Command': 'upload, finalize',
    },
    body: audioFile
  });

  if (!uploadRes.ok) {
    const errText = await uploadRes.text();
    throw new Error(`Falha ao finalizar upload na File API (${uploadRes.status}): ${errText}`);
  }

  const fileInfo = await uploadRes.json();
  const fileUri = fileInfo?.file?.uri;

  if (!fileUri) {
    throw new Error('Gemini File API não retornou o URI do arquivo.');
  }

  console.log(`[TranscriptionService] ✅ Upload concluído. URI: ${fileUri}`);

  // Fase 3: Aguardar o arquivo ficar ACTIVE (processamento do Google)
  const fileName = fileInfo.file.name; // ex: "files/abc123"
  let fileState = fileInfo.file.state;
  let attempts = 0;
  const maxAttempts = 30; // máximo 5 min (10s * 30)

  while (fileState === 'PROCESSING' && attempts < maxAttempts) {
    console.log(`[TranscriptionService] ⏳ Arquivo ainda em processamento (${attempts + 1}/${maxAttempts})...`);
    await new Promise(r => setTimeout(r, 10000));
    
    const statusRes = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/${fileName}?key=${apiKey}`
    );
    if (statusRes.ok) {
      const statusData = await statusRes.json();
      fileState = statusData.state;
    }
    attempts++;
  }

  if (fileState !== 'ACTIVE') {
    throw new Error(`Arquivo de áudio não ficou pronto. Estado atual: ${fileState}`);
  }

  return { fileUri, mimeType };
};

/**
/**
 * Deleta o arquivo temporário da Gemini File API após a transcrição para garantir
 * 100% de sigilo, privacidade clínica e conformidade ética (zero retenção na nuvem).
 */
const deleteGeminiFile = async (fileUri, apiKey) => {
  try {
    const fileId = fileUri.split('/files/')[1] || fileUri.replace(/^files\//, '');
    if (fileId) {
      await fetch(`https://generativelanguage.googleapis.com/v1beta/files/${fileId}?key=${apiKey}`, {
        method: 'DELETE'
      });
      console.log(`[TranscriptionService] 🔒 Arquivo ${fileId} deletado da Gemini File API com sucesso (sigilo total garantido).`);
    }
  } catch (err) {
    console.warn('[TranscriptionService] Não foi possível deletar arquivo temporário da File API:', err?.message || err);
  }
};

/**
 * Chama a API Gemini generateContent usando fileUri (sem base64 no body).
 * Usa o modelo oficial multimodal gemini-1.5-flash: rápido, gratuito e com diarização precisa.
 */
const callGeminiWithFileUri = async (prompt, fileUri, mimeType, modelName, apiKey) => {
  // Lista de modelos ordenados por capacidade e compatibilidade multimodal (gemini-3.8-flash, 3.5-flash, 2.5-flash)
  const candidateModels = [];
  if (modelName && modelName.startsWith('gemini-') && !modelName.includes('1.5') && !modelName.includes('transcribe')) {
    candidateModels.push(modelName);
  }
  candidateModels.push('gemini-3.8-flash', 'gemini-3.5-flash', 'gemini-2.5-flash');

  // Eliminar duplicatas
  const uniqueModels = [...new Set(candidateModels)];
  let lastError = null;

  for (const modelToTry of uniqueModels) {
    try {
      const apiVersion = 'v1beta';
      const url = `https://generativelanguage.googleapis.com/${apiVersion}/models/${modelToTry}:generateContent?key=${apiKey}`;

      const body = {
        contents: [{
          parts: [
            { text: prompt },
            { fileData: { mimeType: mimeType, fileUri: fileUri } }
          ]
        }],
        generationConfig: {
          temperature: 0.1,
          maxOutputTokens: 65536,
        },
        safetySettings: [
          { category: "HARM_CATEGORY_HARASSMENT", threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_HATE_SPEECH", threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_SEXUALLY_EXPLICIT", threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_DANGEROUS_CONTENT", threshold: "BLOCK_NONE" },
        ]
      };

      console.log(`[TranscriptionService] 🚀 Transcrevendo via ${modelToTry} (${apiVersion}) com fileUri...`);

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const errText = await res.text();
        console.warn(`[TranscriptionService] Modelo ${modelToTry} retornou ${res.status}: ${errText}. Tentando próximo modelo...`);
        lastError = new Error(`Erro na API Gemini (${res.status}): ${errText}`);
        continue;
      }

      const data = await res.json();
      const rawText = data?.candidates?.[0]?.content?.parts
        ?.filter(p => p.text)
        ?.map(p => p.text)
        ?.join('') || '';

      if (rawText && rawText.trim()) {
        console.log(`[TranscriptionService] ✅ Transcrição concluída com sucesso via ${modelToTry}!`);
        return rawText;
      }
    } catch (err) {
      console.warn(`[TranscriptionService] Exceção com modelo ${modelToTry}:`, err?.message || err);
      lastError = err;
    }
  }

  throw lastError || new Error('Não foi possível obter a transcrição com os modelos disponíveis.');
};

export const transcribeAudioWithGemini = async (audioFile, options = {}) => {
  console.log('[TranscriptionService] Iniciando transcrição assíncrona com polling via Hermes Bridge...');
  console.log('[TranscriptionService] Arquivo:', audioFile.name, 'MIME:', audioFile.type, 'Tamanho:', (audioFile.size / 1024 / 1024).toFixed(2), 'MB');

  if (typeof options.onProgress === 'function') options.onProgress('Enviando áudio para o servidor...');

  try {
    const isDomain = typeof window !== 'undefined' && window.location.hostname === 'sistema.prosaudemental.com.br';
    const baseUrl = isDomain ? '/api/hermes' : 'https://sistema.prosaudemental.com.br/api/hermes';

    const mimeType = getAudioMimeType(audioFile);
    let jobId;

    if (audioFile.size > 15 * 1024 * 1024) {
      // Áudio longo: envia em partes de 8 MB (o Cloudflare recusa corpos acima de 100 MB);
      // o servidor divide em trechos de 20 min e transcreve um a um
      const startRes = await fetch(`${baseUrl}/transcribe-upload/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mimeType, name: audioFile.name, size: audioFile.size })
      });
      if (!startRes.ok) throw new Error(`Falha ao iniciar envio do áudio (${startRes.status}): ${(await startRes.text()).slice(0, 150)}`);
      const { uploadId } = await startRes.json();
      const CHUNK = 8 * 1024 * 1024;
      const totalChunks = Math.ceil(audioFile.size / CHUNK);
      for (let i = 0; i < totalChunks; i++) {
        if (typeof options.onProgress === 'function') options.onProgress(`Enviando áudio... ${Math.round(((i + 1) / totalChunks) * 100)}%`);
        const blob = audioFile.slice(i * CHUNK, (i + 1) * CHUNK);
        let ok = false;
        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
          try {
            const r = await fetch(`${baseUrl}/transcribe-upload/${uploadId}/chunk/${i}`, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: blob });
            ok = r.ok;
            if (!ok && r.status !== 502 && r.status !== 503) throw new Error(`Falha ao enviar parte ${i + 1} (${r.status}): ${(await r.text()).slice(0, 150)}`);
          } catch (e) {
            if (attempt === 2) throw e;
          }
          if (!ok) await new Promise(res => setTimeout(res, 1500));
        }
        if (!ok) throw new Error(`Falha ao enviar parte ${i + 1} do áudio.`);
      }
      const finishRes = await fetch(`${baseUrl}/transcribe-upload/${uploadId}/finish`, { method: 'POST' });
      if (!finishRes.ok) throw new Error(`Falha ao iniciar transcrição (${finishRes.status}): ${(await finishRes.text()).slice(0, 150)}`);
      ({ jobId } = await finishRes.json());
    } else {
      const base64Audio = await fileToBase64(audioFile);
      // 1. Enviar job assíncrono (retorna em < 1 segundo para evitar timeout 524 do Cloudflare)
      const initRes = await fetch(`${baseUrl}/transcribe-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audioBase64: base64Audio,
          mimeType: mimeType
        })
      });

      if (!initRes.ok) {
        const errText = await initRes.text();
        throw new Error(`Falha ao iniciar transcrição (${initRes.status}): ${errText.slice(0, 150)}`);
      }

      ({ jobId } = await initRes.json());
    }
    console.log(`[TranscriptionService] Job iniciado com sucesso: ${jobId}. Iniciando polling...`);

    // 2. Fazer polling a cada 2.5 segundos do status do job
    const maxAttempts = 2400; // Até 100 minutos (sessões de várias horas são transcritas em partes)
    let attempts = 0;
    let lastProgress = '';

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 2500));
      attempts++;

      if (typeof options.onProgress === 'function') {
        const segs = attempts * 2.5;
        const mins = Math.floor(segs / 60);
        options.onProgress(`${lastProgress || 'Transcrevendo com IA...'} (${mins ? `${mins} min ` : ''}${Math.round(segs % 60)}s)`);
      }

      try {
        const statusRes = await fetch(`${baseUrl}/transcribe-status/${jobId}`);
        if (statusRes.ok) {
          const job = await statusRes.json();
          if (job.progress) lastProgress = job.progress;
          if (job.status === 'completed' && job.text) {
            console.log(`[TranscriptionService] ✅ Transcrição finalizada com sucesso em ${job.elapsedSeconds || '?'}s!`);
            return job.text.trim();
          }
          if (job.status === 'error') {
            throw new Error(job.error || 'Erro interno durante a transcrição do áudio.');
          }
        }
      } catch (pollErr) {
        if (pollErr.message && !pollErr.message.includes('Failed to fetch')) {
          throw pollErr;
        }
      }
    }

    throw new Error('Tempo limite excedido aguardando transcrição.');
  } catch (err) {
    console.error('[TranscriptionService] Erro na transcrição assíncrona:', err);
    throw err;
  }
};

